<template>
  <div class="datetime-input-wrapper" :class="{ 'date-only-mode': isDateOnly }">
    <div
      class="mobile-datetime-trigger"
      :class="{ disabled: disabled || !localeReady }"
      @click="openPicker"
    >
      <span class="mobile-datetime-value" :class="{ 'is-placeholder': !localDateTime }">
        {{ localDateTime ? formatDisplayDate(localDateTime) : mobilePlaceholderText }}
      </span>
      <div class="mobile-datetime-actions">
        <button
          v-if="localDateTime"
          type="button"
          class="mobile-icon-btn"
          :disabled="disabled || !clearable"
          :title="clearText"
          @click.stop="clearDateTime"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11 3L3 11M3 3L11 11"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <button type="button" class="mobile-icon-btn" :disabled="disabled" :title="openPickerText">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5" />
            <path
              d="M8 4V8L11 11"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="showPicker"
        class="mobile-datetime-overlay"
        :class="{ 'desktop-datetime-overlay': !isMobile, 'date-only-picker-overlay': isDateOnly }"
      >
        <div class="mobile-datetime-sheet" :class="{ 'desktop-datetime-sheet': !isMobile }">
          <div class="mobile-datetime-sheet-header">
            <h3>{{ mobileTitleText }}</h3>
            <button
              type="button"
              class="mobile-close-btn"
              :title="cancelText"
              @click="cancelPicker"
            >
              ×
            </button>
          </div>

          <div>
            <VueDatePicker
              v-if="localeReady"
              :key="`mobile-datepicker-${effectiveLocaleString}`"
              v-model="pickerDraftDateTime"
              :disabled="disabled"
              :clearable="false"
              :format="pickerFormat"
              :teleport="false"
              :locale="effectiveLocale"
              :auto-apply="false"
              :enable-time-picker="!isDateOnly && pickerStep === 'time'"
              :time-picker="!isDateOnly && pickerStep === 'time'"
              :inline="true"
              minutes-grid-increment="5"
              @update:model-value="handlePickerModelValueUpdate"
              @internal-model-change="handleInternalModelChange"
            />
          </div>

          <div class="mobile-datetime-sheet-actions">
            <button
              v-if="pickerStep === 'time'"
              type="button"
              class="dp-cancel-btn"
              @click="goToDateStep"
            >
              {{ __('Back') }}
            </button>
            <button
              v-if="pickerDraftDateTime"
              type="button"
              class="dp-cancel-btn"
              :disabled="disabled || !clearable"
              @click="clearAndApplyPicker"
            >
              {{ clearText }}
            </button>
            <button type="button" class="dp-cancel-btn" @click="cancelPicker">
              {{ cancelText }}
            </button>
            <button type="button" class="dp-select-btn" @click="applyPicker">
              {{ selectText }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { VueDatePicker } from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { useCurrentUser } from '@/composables/useCurrentUser'
import { enUS, ru } from 'date-fns/locale'
import { __ } from '@/locale'

const props = defineProps({
  mode: {
    type: String,
    default: 'datetime',
    validator: value => ['datetime', 'date'].includes(value),
  },
  datetime: {
    type: String,
    default: null,
  },
  date: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  locale: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
  autoOpen: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: true,
  },
})

const disabled = computed(() => props.disabled)
const clearable = computed(() => props.clearable)
const isDateOnly = computed(() => props.mode === 'date')

// Get user's preferred locale if not provided via prop
const { userLocale } = useCurrentUser({ removeLoadingOnComplete: false })

// Map locale strings to date-fns locale objects
const localeMap = {
  en: enUS,
  ru: ru,
}

// Get the locale string (for key prop to force re-render)
const effectiveLocaleString = computed(() => {
  return props.locale || userLocale.value || 'en'
})

// Get the date-fns locale object for VueDatePicker
const effectiveLocale = computed(() => {
  const localeString = effectiveLocaleString.value
  return localeMap[localeString] || enUS
})

// Wait for userLocale to be loaded before rendering datepicker
const localeReady = computed(() => {
  return userLocale.value !== null
})

// Translated button texts using i18n
const cancelText = computed(() => {
  return __('Cancel')
})

const selectText = computed(() => {
  return __('Select')
})

const clearText = computed(() => __('Clear'))
const mobilePlaceholderText = computed(() =>
  isDateOnly.value ? __('Select date') : __('Select date and time')
)
const mobileTitleText = computed(
  () => props.title || (isDateOnly.value ? __('Select date') : __('Select date and time'))
)
const openPickerText = computed(() => __('Open'))
const displayTitle = computed(() => props.title)
const pickerFormat = computed(() => (isDateOnly.value ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm'))

const isMobile = ref(false)
const showPicker = ref(false)
const pickerDraftDateTime = ref(null)
const pickerStep = ref('date')
const allowDateStepTransition = ref(false)
const pickerSelectedDate = ref(null)
const pickerSelectedTime = ref(null)

const updateMobileState = () => {
  if (typeof window === 'undefined') return
  isMobile.value = window.innerWidth < 768
}

// Simple function to format date for display
function formatDisplayDate(date) {
  if (!date) return ''
  const locale = effectiveLocaleString.value || 'en'
  const formatter = isDateOnly.value
    ? new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    : new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
  return formatter.format(date)
}

const emit = defineEmits(['update:datetime', 'update:date', 'cancel'])

function clearDateTime() {
  if (!clearable.value) return
  if (isDateOnly.value) {
    emit('update:date', null)
    return
  }
  emit('update:datetime', null)
}

function openPicker() {
  if (disabled.value || !localeReady.value) return
  pickerDraftDateTime.value = localDateTime.value ? new Date(localDateTime.value) : null
  pickerSelectedDate.value = localDateTime.value ? new Date(localDateTime.value) : null
  pickerSelectedTime.value = isDateOnly.value
    ? null
    : localDateTime.value
      ? {
          hours: localDateTime.value.getHours(),
          minutes: localDateTime.value.getMinutes(),
          seconds: localDateTime.value.getSeconds(),
        }
      : null
  pickerStep.value = 'date'
  allowDateStepTransition.value = false
  showPicker.value = true
  requestAnimationFrame(() => {
    allowDateStepTransition.value = true
  })
}

function closePicker() {
  showPicker.value = false
  pickerStep.value = 'date'
  allowDateStepTransition.value = false
  pickerSelectedDate.value = null
  pickerSelectedTime.value = null
}

function parseDateValue(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value)
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    // VueDatePicker may provide structured values depending on mode/model-type.
    if (value.date) {
      const nestedDate = parseDateValue(value.date)
      if (nestedDate) return nestedDate
    }
    if (value.value) {
      const nestedValue = parseDateValue(value.value)
      if (nestedValue) return nestedValue
    }
    if (
      Number.isFinite(Number(value.year)) &&
      Number.isFinite(Number(value.month)) &&
      Number.isFinite(Number(value.day))
    ) {
      const parsedObjectDate = new Date(
        Number(value.year),
        Number(value.month) - 1,
        Number(value.day),
        0,
        0,
        0,
        0
      )
      if (!Number.isNaN(parsedObjectDate.getTime())) return parsedObjectDate
    }
  }

  if (typeof value === 'string') {
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch
      const parsedLocal = new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0)
      if (!Number.isNaN(parsedLocal.getTime())) return parsedLocal
    }
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) return parsed
  }

  if (Array.isArray(value) && value.length > 0) {
    const first = value[0]
    if (first instanceof Date && !Number.isNaN(first.getTime())) return new Date(first)
    if (typeof first === 'string') {
      const parsed = new Date(first)
      if (!Number.isNaN(parsed.getTime())) return parsed
    }
  }

  return null
}

function parseTimeParts(value) {
  const toNumeric = candidate => {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      const parsed = Number(candidate)
      return Number.isFinite(parsed) ? parsed : null
    }
    return null
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const parsed = parseTimeParts(item)
      if (parsed) return parsed
    }
    return null
  }

  const source = value

  if (source instanceof Date && !Number.isNaN(source.getTime())) {
    return {
      hours: source.getHours(),
      minutes: source.getMinutes(),
      seconds: source.getSeconds(),
    }
  }

  if (Array.isArray(source) && source.length >= 2) {
    const [h, m, s] = source
    const hours = toNumeric(h)
    const minutes = toNumeric(m)
    const seconds = toNumeric(s) ?? 0
    if (hours !== null && minutes !== null) {
      return { hours: Number(hours), minutes: Number(minutes), seconds: Number(seconds) }
    }
  }

  if (typeof source !== 'object' || source === null) return null

  const hourValue =
    source.hours?.[0] ??
    source.hour?.[0] ??
    source.h?.[0] ??
    source.hours ??
    source.hour ??
    source.h
  const minuteValue =
    source.minutes?.[0] ??
    source.minute?.[0] ??
    source.m?.[0] ??
    source.minutes ??
    source.minute ??
    source.m
  const secondValue =
    source.seconds?.[0] ??
    source.second?.[0] ??
    source.s?.[0] ??
    source.seconds ??
    source.second ??
    source.s

  const hours = toNumeric(hourValue)
  const minutes = toNumeric(minuteValue)
  const seconds = toNumeric(secondValue) ?? 0

  if (hours === null || minutes === null) return null

  return { hours: Number(hours), minutes: Number(minutes), seconds: Number(seconds) }
}

function normalizeTimeParts(timeParts) {
  if (!timeParts) return null
  const hours = Math.max(0, Math.min(23, Number(timeParts.hours)))
  const minutes = Math.max(0, Math.min(59, Number(timeParts.minutes)))
  const seconds = Math.max(0, Math.min(59, Number(timeParts.seconds ?? 0)))
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) return null
  return { hours, minutes, seconds }
}

function parseAndNormalizeTimeParts(value) {
  return normalizeTimeParts(parseTimeParts(value))
}

function parseAnyDateAndTime(value) {
  if (Array.isArray(value)) {
    let datePart = null
    let timePart = null

    for (const item of value) {
      if (!datePart) {
        const parsedDate = parseDateValue(item)
        if (parsedDate) datePart = parsedDate
      }
      if (!timePart) {
        const parsedTime = parseAndNormalizeTimeParts(item)
        if (parsedTime) timePart = parsedTime
      }
    }

    if (datePart && timePart) {
      const merged = new Date(datePart)
      merged.setHours(timePart.hours, timePart.minutes, timePart.seconds, 0)
      return { iso: merged.toISOString(), time: timePart }
    }
  }

  const asDate = parseDateValue(value)
  if (asDate) {
    return {
      iso: asDate.toISOString(),
      time: {
        hours: asDate.getHours(),
        minutes: asDate.getMinutes(),
        seconds: asDate.getSeconds(),
      },
    }
  }

  const normalizedTime = parseAndNormalizeTimeParts(value)
  if (normalizedTime) {
    const baseDate = pickerSelectedDate.value ? new Date(pickerSelectedDate.value) : new Date()
    if (Number.isNaN(baseDate.getTime())) return null
    baseDate.setHours(normalizedTime.hours, normalizedTime.minutes, normalizedTime.seconds, 0)
    return { iso: baseDate.toISOString(), time: normalizedTime }
  }

  return null
}

function normalizeDraftToIso(value) {
  const parsed = parseAnyDateAndTime(value)
  if (parsed?.time) {
    pickerSelectedTime.value = parsed.time
  }
  return parsed?.iso ?? null
}

function composeIsoFromSelectedParts() {
  if (
    !(pickerSelectedDate.value instanceof Date) ||
    Number.isNaN(pickerSelectedDate.value.getTime())
  ) {
    return null
  }

  const baseDate = new Date(pickerSelectedDate.value)
  const time = normalizeTimeParts(pickerSelectedTime.value)
  if (time) {
    baseDate.setHours(time.hours, time.minutes, time.seconds, 0)
  }
  return baseDate.toISOString()
}

function applyPicker() {
  const value = pickerDraftDateTime.value
  if (isDateOnly.value) {
    const pickedDate =
      (pickerSelectedDate.value instanceof Date && !Number.isNaN(pickerSelectedDate.value.getTime())
        ? pickerSelectedDate.value
        : null) || parseDateValue(value)

    if (pickedDate instanceof Date && !Number.isNaN(pickedDate.getTime())) {
      const dateString = getDateKey(pickedDate)
      emit('update:date', dateString)
    }
    closePicker()
    return
  }

  if (pickerStep.value === 'time') {
    // In time step, prefer already captured reactive time updates.
    // Fallback to parsing current value only when no selected time is known yet.
    if (!normalizeTimeParts(pickerSelectedTime.value)) {
      const parsedTimeFallback = parseAndNormalizeTimeParts(value)
      if (parsedTimeFallback) {
        pickerSelectedTime.value = parsedTimeFallback
      }
    }

    const composedIsoFromTimeStep = composeIsoFromSelectedParts()
    if (composedIsoFromTimeStep) {
      emit('update:datetime', composedIsoFromTimeStep)
      closePicker()
      return
    }
  }

  const normalizedIso = value ? normalizeDraftToIso(value) : null
  if (normalizedIso) {
    emit('update:datetime', normalizedIso)
    closePicker()
    return
  }

  const composedIso = composeIsoFromSelectedParts()
  if (composedIso) {
    emit('update:datetime', composedIso)
    closePicker()
    return
  }

  emit('update:datetime', null)
  closePicker()
}

function clearAndApplyPicker() {
  if (!clearable.value) return
  pickerDraftDateTime.value = null
  if (isDateOnly.value) {
    emit('update:date', null)
    closePicker()
    return
  }
  emit('update:datetime', null)
  closePicker()
}

function getDateKey(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function goToDateStep() {
  pickerStep.value = 'date'
}

function handleInternalModelChange(value) {
  if (!showPicker.value) {
    return
  }

  const timeParts = parseAndNormalizeTimeParts(value)
  if (timeParts) {
    pickerSelectedTime.value = timeParts
  }

  if (pickerStep.value === 'date') {
    if (!allowDateStepTransition.value) return

    const pickedDateCandidate = Array.isArray(value) ? value[0] : value
    const parsedPickedDate = parseDateValue(pickedDateCandidate)
    const dateKey = getDateKey(parsedPickedDate)
    if (!dateKey || !parsedPickedDate) return

    pickerSelectedDate.value = new Date(parsedPickedDate)
    if (isDateOnly.value) return
    pickerStep.value = 'time'
    allowDateStepTransition.value = false
  }
}

function handlePickerModelValueUpdate(value) {
  if (!isDateOnly.value) return
  const parsedDate = parseDateValue(value)
  if (parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime())) {
    pickerSelectedDate.value = new Date(parsedDate)
  }
}

watch(
  () => pickerDraftDateTime.value,
  nextValue => {
    if (!showPicker.value) return

    const parsedTime = parseAndNormalizeTimeParts(nextValue)
    if (parsedTime) {
      pickerSelectedTime.value = parsedTime
    }

    if (pickerStep.value !== 'date') return
    const parsedDate = parseDateValue(nextValue)
    if (parsedDate) {
      pickerSelectedDate.value = new Date(parsedDate)
    }
  }
)

function cancelPicker() {
  closePicker()
  emit('cancel')
}

// Convert ISO datetime string to Date object for UI display/opening draft.
const localDateTime = computed(() => {
  const sourceValue = isDateOnly.value ? props.date : props.datetime
  if (!sourceValue) return null
  const parsed = parseDateValue(sourceValue)
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null
})

watch(
  () => props.autoOpen,
  shouldOpen => {
    if (shouldOpen && localeReady.value && !showPicker.value) {
      openPicker()
    }
  },
  { immediate: true }
)

watch(
  () => localeReady.value,
  isReady => {
    if (isReady && props.autoOpen && !showPicker.value) {
      openPicker()
    }
  }
)

onMounted(() => {
  updateMobileState()
  window.addEventListener('resize', updateMobileState)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateMobileState)
})
</script>

<style scoped>
.datetime-input-wrapper {
  width: 100%;
}

.datetime-input-title {
  display: block;
  margin: 0 0 6px;
  color: #666;
  font-size: 13px;
  line-height: 1.3;
}

.mobile-datetime-trigger {
  width: 100%;
  min-height: 40px;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #fff;
}

.mobile-datetime-trigger.disabled {
  background: #f5f5f5;
  opacity: 0.7;
}

.mobile-datetime-value {
  color: #333;
  font-size: 14px;
  line-height: 1.3;
}

.mobile-datetime-value.is-placeholder {
  color: #666;
}

.mobile-datetime-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.mobile-icon-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #666;
  padding: 4px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  min-width: 30px;
  min-height: 30px;
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    color 0.15s ease;
}

.mobile-icon-btn:hover:not(:disabled) {
  background: #f5f5f5;
  color: #333;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.mobile-icon-btn:active:not(:disabled) {
  background: #eceff3;
}

.mobile-icon-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.mobile-datetime-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
}

.desktop-datetime-overlay {
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.mobile-datetime-sheet {
  width: 100%;
  max-height: 90vh;
  background: #fff;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  padding: 12px 12px 20px;
  padding-bottom: clamp(20px, calc(20px + var(--safe-area-inset-bottom)), 36px);
  overflow-y: auto;
}

.desktop-datetime-sheet {
  width: min(520px, calc(100vw - 32px));
  max-height: min(720px, calc(100vh - 32px));
  border-radius: 16px;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.24);
}

.desktop-datetime-sheet :deep(.dp__main) {
  display: flex;
  justify-content: center;
}

.desktop-datetime-sheet :deep(.dp__instance_calendar) {
  margin-left: auto;
  margin-right: auto;
}

.mobile-datetime-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.mobile-datetime-sheet-header h3 {
  margin: 0;
  font-size: 16px;
}

.mobile-close-btn {
  border: none;
  background: #fff;
  font-size: 24px;
  line-height: 1;
  color: #666;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  padding: 0;
}

.mobile-close-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.mobile-close-btn:focus,
.mobile-close-btn:focus-visible {
  border: none;
  box-shadow: none;
}

.mobile-datetime-sheet-actions {
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  width: 100%;
}

.mobile-datetime-sheet-actions > .dp-cancel-btn,
.mobile-datetime-sheet-actions > .dp-select-btn {
  flex: 1 1 calc(50% - 4px);
  min-width: 0;
  justify-content: center;
  text-align: center;
}

.mobile-datetime-sheet :deep(.dp__action_row) {
  display: none !important;
}

.mobile-datetime-sheet :deep(.dp__selection_preview) {
  display: none !important;
}

/* Override global button styles for ALL datepicker buttons */
:deep(.dp__input_wrap button),
:deep(.dp__input_wrap .dp-clear-btn) {
  background: #fff !important;
  color: #666 !important;
  border: 1px solid #d1d5db !important;
  border-radius: 6px !important;
  padding: 4px !important;
  margin: 0 !important;
  font-weight: normal !important;
  font-size: inherit !important;
  line-height: 1 !important;
  gap: 0 !important;
  min-width: 28px !important;
  min-height: 28px !important;
  width: 28px !important;
  height: 28px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease !important;
}

:deep(.dp__menu .dp__action_button) {
  background: #fff !important;
  color: #333 !important;
  border: 1px solid #d1d5db !important;
}

:deep(.dp__menu .dp__action_button:hover),
:deep(.dp__menu .dp__action_button:focus),
:deep(.dp__menu .dp__action_button:focus-visible) {
  background: #f5f5f5 !important;
  color: #111 !important;
  box-shadow: none !important;
}

:deep(.dp__menu .dp--header-wrap button),
:deep(.dp__menu .dp--header-wrap .dp__btn),
:deep(.dp__menu .dp__btn.dp__inc_dec_button) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

:deep(.dp__menu .dp--header-wrap button:hover),
:deep(.dp__menu .dp--header-wrap .dp__btn:hover),
:deep(.dp__menu .dp__btn.dp__inc_dec_button:hover),
:deep(.dp__menu .dp__btn.dp__inc_dec_button:focus),
:deep(.dp__menu .dp__btn.dp__inc_dec_button:focus-visible) {
  border: none !important;
  background: #f5f5f5 !important;
  box-shadow: none !important;
}

:deep(.dp__input_wrap button:hover),
:deep(.dp__input_wrap .dp-clear-btn:hover) {
  background: #f5f5f5 !important;
  color: #333 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08) !important;
}

:deep(.dp__input_wrap button:active),
:deep(.dp__input_wrap .dp-clear-btn:active) {
  background: #eceff3 !important;
}

:deep(.dp__input_wrap button:focus-visible),
:deep(.dp__input_wrap .dp-clear-btn:focus-visible) {
  outline: 2px solid #2aabee !important;
  outline-offset: 1px !important;
}

/* Input wrapper */
:deep(.dp__input_wrap) {
  width: 100%;
  position: relative;
  overflow: hidden; /* Prevent icons from going outside */
}

/* Input field */
:deep(.dp__input) {
  width: 100%;
  padding-right: 80px; /* Space for both icons */
  box-sizing: border-box;
  caret-color: transparent !important; /* Hide caret since input is readonly */
}

/* Icon container - keep all icons inside the input wrapper */
:deep(.dp__input_icon) {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
}

:deep(.dp__input_icon > *),
:deep(.dp__input_icon button) {
  pointer-events: all;
}

/* Calendar icon (left side) */
:deep(.dp__input_icon:not(.dp__input_clear):not(.dp__input_clock)),
:deep(.dp__input_icon.dp__input_icons),
:deep(.dp__icon.dp__input_icon) {
  left: 8px;
  right: auto;
  padding-left: 4px !important;
  padding-right: 4px !important;
}

/* Clock icon (right side, before clear) */
:deep(.dp__input_icon.dp__input_clock) {
  right: 40px;
  left: auto;
}

/* Clear button (rightmost) */
:deep(.dp__input_icon.dp__input_clear),
:deep(.dp-clear-btn) {
  right: 8px;
  left: auto;
}

/* Only override what's necessary to match app design */
:deep(.dp__input_wrap:focus-within) {
  border-color: #2aabee;
}

:deep(.dp__input_wrap[data-disabled]) {
  background: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}

.date-only-picker-overlay :deep([data-test-id='open-time-picker-btn']),
.date-only-picker-overlay :deep([aria-label='Open time picker']) {
  display: none !important;
}

/* Custom action row wrapper and display */
.dp-action-row-wrapper {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #ddd;
  width: 100%;
}

/* Custom action row buttons */
.dp-action-row {
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  justify-content: center;
  width: 100%;
}

.dp-cancel-btn,
.dp-select-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.15s ease;
}

.dp-cancel-btn {
  background: #fff;
  color: #666;
  border: 1px solid #d1d5db;
}

.dp-cancel-btn:hover {
  background: #f5f5f5;
  color: #333;
  border-color: #c4cad3;
}

.dp-select-btn {
  background: #2aabee;
  color: white;
}

.dp-select-btn:hover {
  background: #2399d6;
}
</style>

<style>
/* Global styles for datepicker buttons - needed because datepicker CSS is loaded globally */
.dp__menu .dp__button:last-child {
  border-radius: 12px;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  border-top-right-radius: 12px;
  border-top-left-radius: 12px;
  margin-bottom: 4px;
}
.dp__menu .dp__button:last-child:hover {
  color: #fff;
}

/* Calendar icon padding */
.dp__input_icon.dp__input_icons,
.dp__icon.dp__input_icon.dp__input_icons,
.dp__input_wrap .dp__input_icon.dp__input_icons {
  padding-left: 4px !important;
  padding-right: 4px !important;
}
</style>
